import React, { useRef } from "react";
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button.jsx";
import { DateTime } from "luxon";
import html2canvas from "html2canvas";

const ShadcnStyleLogsPdf = ({ timezone, secretShopper, community, logs }) => {
  const qrCanvasRef = useRef(null);

  const formatDate = (dateString) => {
    return DateTime.fromISO(dateString)
      .setZone(timezone)
      .toFormat("MMMM dd, yyyy hh:mm a");
  };

  // 1) setHeader
  const setHeader = (pdf, x, y, log, measureOnly = false) => {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);

    let textLines = [];
    let lineHeight = 10;

    if (log.loggable_type === "SecretShopperPhoneLog") {
      const text =
        log.details.from === secretShopper.phone_number
          ? "Secret Shopper To Community"
          : `${log.details.from} To Secret Shopper`;
      textLines.push(text);
    } else if (log.loggable_type === "SecretShopperSmsLog") {
      textLines.push(`Text From ${log.details.from}`);
    } else if (log.loggable_type === "SecretShopperEmailLog") {
      textLines.push(`Email from: ${log.details.sender_email}`);
      textLines.push(`Subject: ${log.details.subject}`);
    } else if (log.loggable_type === "SecretShopperMessageLog") {
      textLines.push(log.details.message || "No Message Available");
    } else {
      textLines.push("No Message Available");
    }

    // Render or measure
    let currentY = y;
    textLines.forEach((txt, i) => {
      if (!measureOnly) {
        pdf.text(txt, x, currentY);
      }
      // Move down by lineHeight
      currentY += i === 0 ? lineHeight : lineHeight - 2;
    });

    return currentY;
  };

  // 2) setLogBody
  const setLogBody = async (pdf, x, y, log, measureOnly = false) => {
    let usedHeight = 0;

    const renderText = (text) => {
      if (!measureOnly) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);
        pdf.text(text, x, y + usedHeight);
      }
      usedHeight += 10; // approximate line height
    };

    if (log.loggable_type === "SecretShopperPhoneLog") {
      if (!log.details.voicemail_recording_signed_url) {
        renderText("No Recording Available");
      } else {
        if (!measureOnly) {
          pdf.setFontSize(9);
          pdf.setTextColor(0, 0, 255);
          pdf.textWithLink(
            "Click to listen to recording",
            x,
            y + usedHeight,
            {
              url: `${process.env.REACT_APP_BASE_URL}/communities/${log.community_id}?tab=secret-shopper`,
            }
          );
          pdf.setTextColor(0, 0, 0);
        }
        usedHeight += 10;
      }
    } else if (log.loggable_type === "SecretShopperSmsLog") {
      const text = log.details.body || "No SMS content available";
      renderText(text);
    } else if (log.loggable_type === "SecretShopperEmailLog") {
        // TODO: fix this emails
    //   if (!measureOnly) {
    //     pdf.setFontSize(9);
    //     pdf.setTextColor(0, 0, 0);
    //     pdf.text("Email Content:", x, y + usedHeight);
    //   }
    //   usedHeight += 10;

    //   const tempDiv = document.createElement("div");
    //   tempDiv.innerHTML = log.details.body || "<div>No email content available</div>";
    //   tempDiv.style.width = "600px";
    //   tempDiv.style.padding = "10px";
    //   tempDiv.style.background = "#ffffff";
    //   tempDiv.style.color = "#000000";
    //   tempDiv.style.position = "absolute";
    //   tempDiv.style.left = "-9999px";
    //   document.body.appendChild(tempDiv);

    //   const canvas = await html2canvas(tempDiv, { scale: 2 });
    //   document.body.removeChild(tempDiv);

    //   const imgData = canvas.toDataURL("image/png");
    //   const imgWidth = 180;
    //   const imgHeight = (canvas.height * imgWidth) / canvas.width;

    //   if (!measureOnly) {
    //     pdf.addImage(imgData, "PNG", x, y + usedHeight, imgWidth, imgHeight);
    //   }
    //   usedHeight += imgHeight + 2;
    } else if (log.loggable_type === "SecretShopperMessageLog") {
      const text = log.details.message || "No details available.";
      renderText(text);
    } else {
      renderText("No additional details available.");
    }

    return y + usedHeight;
  };

  // 3) drawLogCard
  const drawLogCard = async (pdf, x, y, log, cardW) => {
    const pageHeight = pdf.internal.pageSize.getHeight();
    const marginBottom = 15;

    // First pass: measure
    let measureY = y;
    measureY = setHeader(pdf, x + 4, measureY + 6, log, true);
    measureY = await setLogBody(pdf, x + 4, measureY + 3, log, true);
    measureY += 10; // space for created_at

    const cardHeight = measureY - y + 6;

    // Check for page break
    if (y + cardHeight + marginBottom > pageHeight) {
      pdf.addPage();
      y = 20;
      measureY = y;
      measureY = setHeader(pdf, x + 4, measureY + 6, log, true);
      measureY = await setLogBody(pdf, x + 4, measureY + 3, log, true);
      measureY += 10;
    }

    // Draw background
    pdf.setFillColor(240, 240, 240);
    pdf.roundedRect(x, y, cardW, cardHeight, 3, 3, "F");

    // Second pass: actual render
    let renderY = y;
    renderY = setHeader(pdf, x + 4, renderY + 6, log, false);
    renderY = await setLogBody(pdf, x + 4, renderY + 3, log, false);

    // Add date
    pdf.setFontSize(8);
    pdf.setTextColor(80, 80, 80);
    pdf.text(formatDate(log.created_at), x + 4, renderY + 6);

    const extraPaddingBetweenCards = 25;
    return renderY + extraPaddingBetweenCards;
  };

  // 4) generatePdf
  const generatePdf = async () => {
    const pdf = new jsPDF("portrait", "mm", "A4");
    let y = 20;
    const x = 10;
    const cardW = 190;

    // -- Render Secret Shopper header info at top of PDF --
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(0, 0, 0);

    const dateString = `${DateTime.fromISO(secretShopper.created_at).toFormat('MMM dd, yyyy')}-${secretShopper.is_active ? 'Current' : DateTime.fromISO(secretShopper.updated_at).toFormat('MMM dd, yyyy')}`

    pdf.text(`Secret Shopper ${dateString}`, x, y);
    y += 7;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    pdf.text(`First Name: ${secretShopper.first_name || "N/A"}`, x, y);
    y += 6;
    pdf.text(`Last Name: ${secretShopper.last_name || "N/A"}`, x, y);
    y += 6;
    pdf.text(`Phone: ${secretShopper.phone_number || "N/A"}`, x, y);
    y += 6;
    pdf.text(`Email: ${secretShopper.email || "N/A"}`, x, y);
    y += 10;

    // -- Now render each log --
    for (const log of logs) {
      y = await drawLogCard(pdf, x, y, log, cardW);
    }

    pdf.save(`Secret_Shopper_${dateString}.pdf`);
  };

  return (
    <div>
      <Button variant="outline" onClick={generatePdf}>Download</Button>
      <canvas ref={qrCanvasRef} style={{ display: "none" }} />
    </div>
  );
};

export default ShadcnStyleLogsPdf;
